// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadInvoices from "@insite/client-framework/Store/Data/Invoices/Handlers/LoadInvoices";
import { getInvoicesDataView } from "@insite/client-framework/Store/Data/Invoices/InvoicesSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CardListStyles } from "@insite/content-library/Components/CardList";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import HomeAccordion from "@steiner/App/Components/Home/HomeAccordion";
import Notify, { getNotifyStyle } from "@steiner/App/Components/Notify";
import InvoiceSummaryCard, { InvoiceSummaryCardStyles } from "@steiner/App/Widgets/OpenInvoices/InvoiceSummaryCard";
import { labels } from "@steiner/Overrides/Widgets/InvoiceHistory/InvoiceHistoryTable";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    numberOfRecords = "numberOfRecords",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    return {
        settingsCollection: getSettingsCollection(state),
        session,
        invoicesDataView: getInvoicesDataView(state, openInvoicesParameter),
        myInvoicePageNavLink: getPageLinkByPageType(state, "InvoiceHistoryPage"),
        canViewInvoices: isAuthenticated,
    };
};

const mapDispatchToProps = {
    loadInvoices,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OpenInvoicesStyles {
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableHeaders?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    dataTableCells?: DataTableCellBaseProps;
    dataCell?: DataTableCellProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyPresentationProps;
    invoiceSummaryCard?: InvoiceSummaryCardStyles;
}

export const recentQuotesStyles: OpenInvoicesStyles = {};

const openInvoicesParameter = {
    page: 1,
    pageSize: 5,
    customerSequence: "-1",
    showOpenOnly: true,
};

const styles = recentQuotesStyles;

class OpenInvoices extends React.Component<Props> {
    componentDidMount() {
        if (this.props.canViewInvoices && !this.props.invoicesDataView.value) {
            openInvoicesParameter.pageSize = this.props.fields.numberOfRecords || openInvoicesParameter.pageSize;
            this.props.loadInvoices(openInvoicesParameter);
        }
    }

    render() {
        const { settingsCollection, session, invoicesDataView, myInvoicePageNavLink, canViewInvoices } = this.props;

        if (!canViewInvoices || !invoicesDataView.value) {
            return null;
        }

        if (invoicesDataView.value.length === 0) {
            return null;
        }

        return (
            <HomeAccordion
                title={translate("Open Invoices")}
                isLoading={invoicesDataView.isLoading}
                pagination={invoicesDataView.pagination}
                itemsCount={invoicesDataView.value.length || 0}
                viewAll={myInvoicePageNavLink?.url}
            >
                {invoicesDataView.value.length === 0 && (
                    <StyledWrapper {...styles.noResultsContainer}>
                        <Typography {...styles.noResultsText}>{translate("No invoices found")}</Typography>
                    </StyledWrapper>
                )}
                {invoicesDataView.value.length > 0 && (
                    <DataTable {...styles.dataTable}>
                        <DataTableHead {...styles.dataTableHead}>
                            <DataTableHeader {...styles.dataTableHeaders}>{labels.invoiceDate}</DataTableHeader>
                            <DataTableHeader {...styles.dataTableHeaders}>{labels.invoiceNumber}</DataTableHeader>
                            <DataTableHeader {...styles.dataTableHeaders}>{labels.dueDate}</DataTableHeader>
                            <DataTableHeader {...styles.dataTableHeaders}>{labels.invoiceTotal}</DataTableHeader>
                            <DataTableHeader {...styles.dataTableHeaders}>{labels.actions}</DataTableHeader>
                        </DataTableHead>
                        <DataTableBody {...styles.dataTableBody}>
                            {invoicesDataView.value.map(
                                invoice =>
                                    invoice !== null && (
                                        <DataTableRow
                                            {...styles.dataTableRow}
                                            data-test-selector="openInvoices_dataLine"
                                            key={invoice.id}
                                        >
                                            <InvoiceSummaryCard
                                                invoice={invoice}
                                                session={session}
                                                invoiceSettings={settingsCollection.invoiceSettings}
                                                extendedStyles={styles.invoiceSummaryCard}
                                            />
                                        </DataTableRow>
                                    ),
                            )}
                        </DataTableBody>
                    </DataTable>
                )}
            </HomeAccordion>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OpenInvoices),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.numberOfRecords,
                displayName: "Number of Recent Invoices Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 5,
                fieldType: "General",
                sortOrder: 1,
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
